// material-ui
import {styled, useTheme} from '@mui/material/styles';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';

// third-party
import {useDropzone} from 'react-dropzone';

// project-imports
import RejectionFiles from './RejectionFiles';
import PlaceholderContent from './PlaceholderContent';

// types
import {CustomFile, UploadProps} from 'types';

const DropzoneWrapper = styled('div')(({theme}) => ({
    outline: 'none',
    overflow: 'hidden',
    position: 'relative',
    padding: theme.spacing(5, 1),
    borderRadius: theme.shape.borderRadius,
    transition: theme.transitions.create('padding'),
    backgroundColor: theme.palette.background.paper,
    border: '1px dashed',
    borderColor: theme.palette.secondary.main,
    '&:hover': {opacity: 0.72, cursor: 'pointer'},
}));

// ==============================|| UPLOAD - SINGLE FILE (Image, CSV, XLSX) ||============================== //

export const SingleFileUpload: React.FC<UploadProps> = ({error, file, setFieldValue, sx, maxHeight}) => {
    const theme = useTheme();

    const {getRootProps, getInputProps, isDragActive, isDragReject, fileRejections} = useDropzone({
        accept: {
            'image/*': [],
            'text/csv': ['.csv'],
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
        },
        multiple: false,
        onDrop: (acceptedFiles: CustomFile[]) => {
            setFieldValue(
                'files',
                acceptedFiles.map((file: CustomFile) => Object.assign(file, {preview: URL.createObjectURL(file)})),
            );
        },
    });

    const thumbs =
        file &&
        file.map((item: CustomFile) => (
            <img
                key={item.name}
                alt={item.name}
                src={item.preview}
                style={{
                    top: 8,
                    left: 8,
                    borderRadius: 2,
                    position: 'absolute',
                    width: 'calc(100% - 16px)',
                    height: 'calc(100% - 16px)',
                    background: theme.palette.background.paper,
                }}
                onLoad={() => {
                    URL.revokeObjectURL(item.preview!);
                }}
            />
        ));

    const onRemove = () => {
        setFieldValue('files', null); // Remove the file from the form state
        // onFileChange(null); // Notify parent that the file was removed
    };

    return (
        <Box sx={{width: '100%', ...sx}}>
            <DropzoneWrapper
                {...getRootProps()}
                sx={{
                    maxHeight: maxHeight,
                    ...(isDragActive && {opacity: 0.72}),
                    ...((isDragReject || error) && {
                        color: 'error.main',
                        borderColor: 'error.light',
                        bgcolor: 'error.lighter',
                    }),
                    ...(file && {padding: '12% 0'}),
                }}
            >
                <input {...getInputProps()} />
                <PlaceholderContent />
                {thumbs}
            </DropzoneWrapper>

            {fileRejections.length > 0 && <RejectionFiles fileRejections={[...fileRejections]} />}

            {file && file.length > 0 && (
                <Stack direction="row" justifyContent="flex-end" sx={{mt: 1.5}}>
                    <Button variant="contained" color="error" onClick={onRemove}>
                        Remove
                    </Button>
                </Stack>
            )}
        </Box>
    );
};

export default SingleFileUpload;
